import React, { useRef, useState } from "react";
import { api, API, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ImagePlus, Loader2 } from "lucide-react";

const BASE = (API || "").replace(/\/api$/, "");

/** Resolve a stored design_image_url (relative /api/files/{id} or absolute). */
export function designImageSrc(url) {
  if (!url) return null;
  return url.startsWith("http") ? url : `${BASE}${url}`;
}

/**
 * Project design image. Read-only by default; pass editable to show an
 * upload/replace control (POST /uploads -> PATCH /projects/{id}).
 */
export default function DesignImage({ project, editable = false, onChange, className = "", height = "h-48" }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const src = designImageSrc(project?.design_image_url);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file (PNG/JPG)"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const up = await api.post(`/uploads?project_id=${project.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = up.data.download_url;
      await api.patch(`/projects/${project.id}`, { design_image_url: url });
      toast.success("Design image updated");
      onChange?.(url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`swiss-card-flat overflow-hidden ${className}`} data-testid="design-image">
      {src ? (
        <img src={src} alt="System design" className={`w-full ${height} object-cover`} />
      ) : (
        <div className={`w-full ${height} flex flex-col items-center justify-center gap-2 bg-[var(--surface)] muted-x`}>
          <ImagePlus size={26} />
          <span className="label">No design image</span>
        </div>
      )}
      {editable && (
        <div className="flex items-center justify-between px-3 py-2 border-t" style={{ borderColor: "var(--line)" }}>
          <span className="label">Design image</span>
          <button className="btn-ghost text-xs" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="design-image-upload-btn">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />}
            {src ? "Replace" : "Upload"}
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} />
        </div>
      )}
    </div>
  );
}
